// Watch mode's lock — the record that says this browser is showing one panel
// to a room (`todo-takeout.md` entry 6.25).
//
// Key exports: readWatchLock, isWatching, setWatchLock, clearWatchLock,
// verifyWatchPassphrase.
// Neighbours: state/useWatchMode.ts (the React store over this),
// lib/serverFetch.ts (refuses a mutation while this is set),
// shell/WatchLayout.tsx (the screen), groups/takeout (the panel it was built
// for).
//
// **Plain module, not a store, because the write path is not React.** Every
// mutation in this app goes through `serverFetch`, which is a function and not
// a component; a lock it could only read through a hook would be a lock the
// hidden navigation was the whole of.
//
// **It survives a reload, which is the point.** A passer-by pressing F5 or
// opening a new tab must land back on the same panel, so the lock lives in
// `localStorage` rather than in memory or in `sessionStorage`. Signing out
// clears it — that is the documented way back for somebody who has forgotten
// the passphrase.
//
// **The passphrase is never stored.** A salted digest is, and the salt is per
// lock so two booths choosing "1234" do not produce the same record.
//
// > ⚠️ **This protects a screen, not an account.** Someone holding the laptop
// > can clear site data and be back at a signed-in portal. That is by design
// > (entry 6.25): the passphrase is set per lock and guards re-entry on this
// > device, and it is not a second credential.

const LS_WATCH_LOCK = 'synvya_watch_lock'

/** How a stored digest was produced. A digest is only ever compared against
 *  one recomputed with the same algorithm. */
export type WatchDigestAlgorithm = 'sha-256' | 'fnv-1a'

export interface WatchLock {
  /** The route this session is locked on. Every other route redirects here. */
  panel: string
  /** Random per lock. Salted so one passphrase does not have one digest. */
  salt: string
  digest: string
  algorithm: WatchDigestAlgorithm
  /** When the lock was set, ISO. The unlock prompt says it. */
  lockedAt: string
}

function isWatchLock(value: unknown): value is WatchLock {
  if (typeof value !== 'object' || value === null) return false
  const lock = value as Record<string, unknown>
  return (
    typeof lock.panel === 'string' &&
    lock.panel.startsWith('/') &&
    typeof lock.salt === 'string' &&
    typeof lock.digest === 'string' &&
    (lock.algorithm === 'sha-256' || lock.algorithm === 'fnv-1a') &&
    typeof lock.lockedAt === 'string'
  )
}

/**
 * The lock this browser holds, or null.
 *
 * **A record that does not parse is no lock.** Storage can be cleared, edited
 * by hand, or written by an older version of this file; treating a malformed
 * one as locked would strand a merchant on a screen with no passphrase that
 * opens it, which is worse than the lock failing open.
 */
export function readWatchLock(): WatchLock | null {
  try {
    const raw = localStorage.getItem(LS_WATCH_LOCK)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isWatchLock(parsed) ? parsed : null
  } catch {
    return null
  }
}

/** True while this browser is showing one panel to a room. */
export function isWatching(): boolean {
  return readWatchLock() !== null
}

export function writeWatchLock(lock: WatchLock): void {
  try {
    localStorage.setItem(LS_WATCH_LOCK, JSON.stringify(lock))
  } catch {
    // Private mode, or storage full. The caller checks `readWatchLock` back,
    // so a lock that could not be written is reported rather than pretended.
  }
}

export function clearWatchLock(): void {
  try {
    localStorage.removeItem(LS_WATCH_LOCK)
  } catch {
    // Nothing to do. A lock that cannot be cleared is cleared by signing out.
  }
}

function toHex(bytes: ArrayBuffer): string {
  return Array.from(new Uint8Array(bytes))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * A non-cryptographic digest, used only where `crypto.subtle` is absent.
 *
 * **That is local development, not production.** `crypto.subtle` needs a
 * secure context, and this workspace's local hosts are plain HTTP under
 * `*.local.synvya.com`. Every deployed surface is HTTPS and takes the SHA-256
 * path. Refusing to lock without `subtle` would make the feature untestable
 * locally; storing the passphrase itself would be worse than a weak digest.
 */
function fnv1a(input: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}

/** Random hex, from the platform's generator where there is one. */
function randomSalt(): string {
  const values = new Uint8Array(16)
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(values)
  } else {
    for (let i = 0; i < values.length; i++) values[i] = Math.floor(Math.random() * 256)
  }
  return toHex(values.buffer)
}

/** Digest `passphrase` under `salt`, with the best algorithm available here. */
export async function digestPassphrase(
  salt: string,
  passphrase: string,
  algorithm?: WatchDigestAlgorithm,
): Promise<{ digest: string; algorithm: WatchDigestAlgorithm }> {
  const input = `${salt}:${passphrase}`
  const subtle = globalThis.crypto?.subtle
  const wanted = algorithm ?? (subtle ? 'sha-256' : 'fnv-1a')

  if (wanted === 'sha-256') {
    if (!subtle) {
      // Asked for a digest this context cannot produce. Answering with the
      // other algorithm would compare two different functions and unlock
      // nothing, for ever.
      throw new Error('SHA-256 is not available in this context.')
    }
    const bytes = new TextEncoder().encode(input)
    return { digest: toHex(await subtle.digest('SHA-256', bytes)), algorithm: 'sha-256' }
  }

  return { digest: fnv1a(input), algorithm: 'fnv-1a' }
}

/**
 * Lock this browser on `panel`.
 *
 * Returns the lock that was written, so a caller can report a storage that
 * refused it rather than showing a locked screen nothing recorded.
 */
export async function setWatchLock(panel: string, passphrase: string): Promise<WatchLock | null> {
  const salt = randomSalt()
  const { digest, algorithm } = await digestPassphrase(salt, passphrase)
  const lock: WatchLock = { panel, salt, digest, algorithm, lockedAt: new Date().toISOString() }
  writeWatchLock(lock)
  return readWatchLock()
}

/**
 * Does `passphrase` open the lock this browser holds?
 *
 * **Recomputed under the stored algorithm**, never the one this context would
 * pick now: a lock set over HTTPS and read in a context without `crypto.subtle`
 * must compare SHA-256 against SHA-256 or refuse, not silently compare two
 * different functions and never open.
 *
 * The comparison is not constant-time. The secret is a booth passphrase on a
 * device the attacker is standing in front of; a timing oracle is not the way
 * in, and pretending otherwise would be security theatre.
 */
export async function verifyWatchPassphrase(passphrase: string): Promise<boolean> {
  const lock = readWatchLock()
  if (!lock) return true
  try {
    const { digest } = await digestPassphrase(lock.salt, passphrase, lock.algorithm)
    return digest === lock.digest
  } catch {
    return false
  }
}
